import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import type { AnatomyState } from "../QuestionProvider/types";

type AnatomyQuestionSliceState = Record<string, AnatomyState>;

const getDefaultState = (): AnatomyState => ({
  answers: {},
  correctAnswers: [],
  incorrectAnswers: [],
  isSubmitted: false,
  showFeedback: false,
});

const anatomyQuestionSlice = createSlice({
  name: "anatomy",
  initialState: {} as AnatomyQuestionSliceState,
  reducers: {
    setAnswer: (
      state,
      action: PayloadAction<{
        componentID: string;
        calloutId: string;
        value: string;
      }>
    ) => {
      const { componentID, calloutId, value } = action.payload;
      if (!state[componentID]) {
        state[componentID] = getDefaultState();
      }
      state[componentID].answers[calloutId] = value;
      state[componentID].incorrectAnswers = state[
        componentID
      ].incorrectAnswers.filter((id) => id !== calloutId);
      if (state[componentID].incorrectAnswers.length === 0) {
        state[componentID].showFeedback = false;
      }
    },
    submit: (
      state,
      action: PayloadAction<{
        componentID: string;
        callouts: { id: string; correctAnswer: string }[];
      }>
    ) => {
      const { componentID, callouts } = action.payload;
      if (!state[componentID]) {
        state[componentID] = getDefaultState();
      }
      const current = state[componentID];
      const correct: string[] = [];
      const incorrect: string[] = [];
      callouts.forEach((callout) => {
        if (current.answers[callout.id] === callout.correctAnswer) {
          correct.push(callout.id);
        } else {
          incorrect.push(callout.id);
        }
      });
      state[componentID].isSubmitted = true;
      state[componentID].correctAnswers = correct;
      state[componentID].incorrectAnswers = incorrect;
      state[componentID].showFeedback = true;
    },
    reset: (state, action: PayloadAction<string>) => {
      const componentID = action.payload;
      state[componentID] = getDefaultState();
    },
  },
  extraReducers: (builder) => {
    const isLegacy = (action: any, type: string) =>
      action.type === type && !action._fromTranslator;

    builder
      .addMatcher(
        (action) => isLegacy(action, "ANATOMY/Set Answer"),
        (state, action: any) => {
          const { componentID, calloutId, value } = action.payload;
          if (!state[componentID]) {
            state[componentID] = getDefaultState();
          }
          state[componentID].answers[calloutId] = value;
          state[componentID].incorrectAnswers = state[
            componentID
          ].incorrectAnswers.filter((id) => id !== calloutId);
          if (state[componentID].incorrectAnswers.length === 0) {
            state[componentID].showFeedback = false;
          }
        }
      )
      .addMatcher(
        (action) => isLegacy(action, "ANATOMY/Submit"),
        (state, action: any) => {
          const { componentID, callouts } = action.payload;
          if (!state[componentID]) {
            state[componentID] = getDefaultState();
          }
          const current = state[componentID];
          const correct: string[] = [];
          const incorrect: string[] = [];
          callouts.forEach((callout: { id: string; correctAnswer: string }) => {
            if (current.answers[callout.id] === callout.correctAnswer) {
              correct.push(callout.id);
            } else {
              incorrect.push(callout.id);
            }
          });
          state[componentID].isSubmitted = true;
          state[componentID].correctAnswers = correct;
          state[componentID].incorrectAnswers = incorrect;
          state[componentID].showFeedback = true;
        }
      )
      .addMatcher(
        (action) => isLegacy(action, "ANATOMY/Reset"),
        (state, action: any) => {
          const componentID = action.payload;
          state[componentID] = getDefaultState();
        }
      );
  },
});

export const anatomyQuestionActions = anatomyQuestionSlice.actions;
export const anatomyQuestionReducer = anatomyQuestionSlice.reducer;

export const selectAnatomyQuestionState = (
  state: { anatomy: AnatomyQuestionSliceState },
  componentId: string
) => state.anatomy?.[componentId] || getDefaultState();

/** @deprecated Use anatomyQuestionActions */
export const anatomyActions = anatomyQuestionActions;
/** @deprecated Use anatomyQuestionReducer */
export const anatomySliceReducer = anatomyQuestionReducer;
/** @deprecated Use selectAnatomyQuestionState */
export const selectAnatomyState = selectAnatomyQuestionState;
